import Vue from "vue";
import _ from "lodash";
import moment from "moment";
import {
    FetchState,
    TransactionResponse,
    HsaDailyBalanceState,
    HsaDailyBalance,
    BankDailyBalanceRequest,
    BankTransactionDailyBalancePage,
    ExtendedContribution,
    PayrollContribution,
    BalanceResponse,
    BalanceType,
    FetchStateKey,
    BankDailyBalanceRecord
} from "@/shared/types";
import { fetchStateKeyMatch } from "@/shared/fetchStateHelper";
import * as storeHelper from "@/shared/storeHelper";
import * as bankService from "@/shared/api/bankService";
import * as payrollService from "@/shared/api/payrollService";
import { isProd } from "@/shared/env";
import { errorIsStatus } from "@/shared/errorHelper";
import { defineStore } from "pinia";

export const initialState: HsaDailyBalanceState = {
    fetchState: FetchState.Uninitialized,
    fetchKey: null,
    predictedDailyBalancesFetchState: FetchState.Uninitialized,
    dailyBalances: [],
    dailyBalancesDictionary: {},
    predictedDailyBalances: [],
    dailyBalancesTransactionsDictionary: {},
    tooltipDate: null,
    currentEndDateKey: null,
    accountStartDate: "",
    periodStartBalance: 0,
    balanceOnFile: {
        fetchState: FetchState.Uninitialized,
        asOfDate: null,
        amount: 0
    },
    instantBalance: {
        fetchState: FetchState.Uninitialized,
        amount: null,
        fetchKey: null,
        amountAsOfDate: null
    },
    doesHsaAccountExist: false
};

const currentDateFormat = "DD-MMM-YY";

export const useHsaDailyBalanceStore = defineStore("hsaDailyBalance", {
    state: (): HsaDailyBalanceState => (_.cloneDeep(initialState)),
    getters: {
        currentDateFormat () {
          return currentDateFormat;
        },
        isInstantBalancesLoaded: state => state.fetchState === FetchState.Loaded,
        balancesForCurrentDates () {
            const unmodifiedDailyBalances: HsaDailyBalance[] = (
                (this.dailyBalancesDictionary && this.dailyBalancesDictionary[this.currentEndDateKey])
                    ? this.dailyBalancesDictionary[this.currentEndDateKey]
                    : null
            );

            const pendingAmount = this.getInstantBalance.amount && (this.pendingAmount || 0);
            if (unmodifiedDailyBalances && pendingAmount) {
                // incorporate the webservice "instant balance" into the graph data
                // by either updating the current days balance or appending a new point.

                // Expand the date range to attempt to account for west coast users late in the day or early in the morning
                const impact = moment().add(-9, "hours").startOf("day").unix();

                if (unmodifiedDailyBalances.some(v => v.timestamp > impact)) {
                    return unmodifiedDailyBalances.map(v => {
                        return {
                            ...v,
                            balance: (v.timestamp > impact) ? this.getInstantBalance.amount : v.balance
                        };
                    });
                }

                // it's possible the user clicked "page back" and the data set
                // under investigation is for a prior period of time which should not
                // be impacted by the current webservice balance.
                if (moment(this.currentEndDateKey, "YYYYMMDD").unix() >= impact) {
                    return [ ...unmodifiedDailyBalances, {
                        balance: this.getInstantBalance.amount,
                        date: moment().format(currentDateFormat)
                    } ];
                }
            }

            return unmodifiedDailyBalances;
        },
        getterPredictedDailyBalances: state =>
            (state.predictedDailyBalancesFetchState === FetchState.Loaded ? state.predictedDailyBalances : null),
        transactionsForCurrentDate () {
            if (!this.dailyBalancesTransactionsDictionary) return null;

            const returnVal = this.dailyBalancesTransactionsDictionary[this.tooltipDate] ?
                this.dailyBalancesTransactionsDictionary[this.tooltipDate] : null;

            if (this.tooltipDate !== moment().format("YYYYMMDD")
                || this.pendingAmount === false) {
                return returnVal;
            }

            return [ ...(returnVal || []), {
                source: "other", displaySource: "Other", amount: this.pendingAmount
            } ];
        },
        instantBalanceLoaded: state => state.instantBalance.fetchState === FetchState.Loaded,
        getInstantBalance: state => state.instantBalance.fetchState === FetchState.Loaded ?
            { amount: state.instantBalance.amount, loading: false } : { loading: true, amount: 0 },
        balanceOnFileLoaded: state => {
            return state.balanceOnFile.fetchState === FetchState.Loaded;
        },
        balanceOnFileAmount: state => {
            if (state.balanceOnFile.fetchState === FetchState.Loaded) {
                return state.balanceOnFile.amount;
            }
            return 0;
        },
        balanceOnFileDate: state => {
            if (state.balanceOnFile.fetchState === FetchState.Loaded) {
                return state.balanceOnFile.asOfDate;
            }
            return null;
        },
        pendingAmount () {
            if (!this.getInstantBalance.loading) {
                // the daily balance for today or yesterday should exist as a key in the dictionary
                return this.getInstantBalance.amount - this.balanceOnFileAmount;
            }
            return false;
        },
        nextRecurringContribution: state => state.predictedDailyBalances && state.predictedDailyBalances[0]
            && state.predictedDailyBalances[0].employeeRecurringAmount
        ,
        isHsaAccountReady: state => state.doesHsaAccountExist
    },
    actions: {
        setStateLoading (key: FetchStateKey): void {
            this.fetchState = FetchState.Loading;
            this.fetchKey = key;
        },
        invalidateBalanceFetchState (key: FetchStateKey) {
            if (fetchStateKeyMatch(key, this.fetchKey)) {
                return;
            }
            this.dailyBalances = [];
            this.dailyBalancesDictionary = {};
            this.balanceOnFile = {
                fetchState: FetchState.Uninitialized,
                asOfDate: null,
                amount: 0
            };
            this.fetchState = FetchState.Uninitialized;
        },
        invalidateBalanceOnFileFetchState () {
            this.balanceOnFile = {
                fetchState: FetchState.Uninitialized,
                asOfDate: null,
                amount: 0
            };
        },
        setPredictedBalancesStateLoading (): void {
            this.predictedDailyBalancesFetchState = FetchState.Loading;
        },
        setPredictedBalancesStateUnloaded () {
            this.predictedDailyBalancesFetchState = FetchState.Uninitialized;
        },
        setDailyBalancesState (
            mutationArguments: {
                stateKey: FetchStateKey, balanceData: BankTransactionDailyBalancePage
            }): void {

            if (this.fetchState !== FetchState.Loading ||
                !fetchStateKeyMatch(this.fetchKey, mutationArguments.stateKey)) {
                return;
            }
            const data = mutationArguments.balanceData;
            const records = _.sortBy(data.records.map(x => {
                const momentInst = moment(x.date);
                return {
                    ...x,
                    timestamp: momentInst.unix(),
                    date: momentInst.format(currentDateFormat)
                };
            }), [ "timestamp" ]);

            this.fetchState = FetchState.Loaded;
            this.dailyBalances = [ ...this.dailyBalances, ...records ];
            Vue.set(this.dailyBalancesDictionary, `${this.currentEndDateKey}`, records);
            this.periodStartBalance = data.periodStartBalance;
            this.accountStartDate = data.accountStartDate;
        },
        setPredictedDailyBalances (data: PayrollContribution[]): void {
            this.predictedDailyBalancesFetchState = FetchState.Loaded;

            const mapped = data.map(original => {
                const x: ExtendedContribution = {
                    ...original,
                    balance: original.total,
                    date: moment(original.date).format(currentDateFormat)
                };

                return x;
            });

            this.predictedDailyBalances = mapped;
        },
        setDailyBalancesTransactionsDictionary (data: TransactionResponse): void {
            this.fetchState = FetchState.Loaded;
            Vue.set(this.dailyBalancesTransactionsDictionary, `${this.tooltipDate}`, data.records);
        },
        setPeriodStartBalance (balance: number): void {
            this.periodStartBalance = balance;
        },
        setAccountStartDate (accountStartDate: string): void {
            this.accountStartDate = accountStartDate;
        },
        setTooltipDate (date: string): void {
            this.tooltipDate = date;
        },
        setCurrentEndDateKey (currentEndDateKey: string): void {
            this.currentEndDateKey = currentEndDateKey;
        },
        setInstantBalance (mutationArguments: { stateKey: FetchStateKey, balanceData: BalanceResponse }) {
            const balances = mutationArguments.balanceData.balances;

            if (!fetchStateKeyMatch(this.instantBalance.fetchKey, mutationArguments.stateKey)) {
                return;
            }

            const balanceToUse = balances.find(value => value.type === BalanceType.Available);
            if (balanceToUse) {
                this.instantBalance.amount = balanceToUse.amount > 0 ? balanceToUse.amount : 0;
                this.instantBalance.amountAsOfDate = moment().format("MMM D, YYYY");
            }
            this.instantBalance.fetchState = FetchState.Loaded;
        },
        setInstantBalanceLoading (key: FetchStateKey) {
            this.instantBalance.fetchKey = key;
            this.instantBalance.fetchState = FetchState.Loading;
        },
        invalidateInstantBalanceFetchState (key: FetchStateKey) {
            if (fetchStateKeyMatch(key, this.instantBalance.fetchKey)) {
                return;
            }
            this.instantBalance.amount = null;
            this.instantBalance.amountAsOfDate = null;
            this.instantBalance.fetchKey = null;
            this.instantBalance.fetchState = FetchState.Uninitialized;
        },
        setHsaAccountStatus (mutationArguments: { stateKey: FetchStateKey, accountStatus: boolean }) {
            if (fetchStateKeyMatch(this.instantBalance.fetchKey, mutationArguments.stateKey)) {
                this.doesHsaAccountExist = mutationArguments.accountStatus;
            }
        },
        setBalanceOnFileLoading (stateKey: FetchStateKey) {
            this.balanceOnFile.fetchState = FetchState.Loading;
            this.fetchKey = stateKey;
        },
        setBalanceOnFile (data) {
            this.balanceOnFile.amount = data.balance;
            this.balanceOnFile.asOfDate = data.date;
            this.balanceOnFile.fetchState = FetchState.Loaded;
        },
        loadInstantBalance (options: {
            force?: boolean,
            username?: string,
            partnerId?: number
        }) {
            options = options || {};

            const fetchStateKey: FetchStateKey = {
                username: options.username,
                partnerId: options.partnerId
            };

            this.invalidateInstantBalanceFetchState(fetchStateKey);

            const force = options && (options.force === true);
            if (this.instantBalance.fetchState === FetchState.Uninitialized || force) {
                // during force update -- allowing the instant balance to move to an unloaded state
                // causes some of the graphs and numbers to jump back and forth.
                // if it's already loaded don't set to unloaded during force update
                if (!(force && this.instantBalance.fetchState === FetchState.Loaded)) {
                    this.setInstantBalanceLoading(fetchStateKey);
                }

                bankService.getInstantBalance(options.username, options.partnerId)
                    .then(response => response.data)
                    .then(balanceData => {
                        this.setHsaAccountStatus({ stateKey: fetchStateKey, accountStatus: true });
                        this.setInstantBalance({ stateKey: fetchStateKey, balanceData });
                    })
                    .catch(err => {
                        if (!isProd() || errorIsStatus(err, 404)) {
                            this.handleNoInstantDailyBalance(fetchStateKey);
                            return;
                        }
                        return;
                    });
            }
        },
        loadDailyBalances (dateParams: BankDailyBalanceRequest): Promise<void> {
            const fetchStateKey: FetchStateKey = {
                username: dateParams.username,
                partnerId: dateParams.partnerId
            };

            this.invalidateBalanceFetchState(fetchStateKey);

            // already loading ANYTHING then ditch out
            if (this.fetchState === FetchState.Loading) {
                return Promise.resolve();
            }

            // assume page in dictionary is a loaded page
            if (this.dailyBalancesDictionary &&
                this.dailyBalancesDictionary[this.currentEndDateKey]) return Promise.resolve();

            this.setStateLoading(fetchStateKey);

            return bankService.getDailyBalances(dateParams.startDate, dateParams.endDate, dateParams.username,
                    dateParams.partnerId)
                .then(response => {
                    const records: BankDailyBalanceRecord[] = _.get(response, "data.records", []);

                    const currentDate = moment();
                    const paramEndDate = moment(dateParams.endDate);
                    const useParamEndDate = paramEndDate.isBefore(currentDate);

                    if (records.length === 0) {
                        this.handleNoDailyBalanceRecords({
                            stateKey: fetchStateKey,
                            firstRecordDate: useParamEndDate ? paramEndDate.format("YYYY-MM-DD") : null,
                            accountStartDate: response.data.accountStartDate,
                            periodStartBalance: response.data.periodStartBalance
                        });
                    } else {
                        this.setHsaAccountStatus({ stateKey: fetchStateKey, accountStatus: true });
                        this.setDailyBalancesState({ stateKey: fetchStateKey, balanceData: response.data });
                    }
                })
                .catch(err => {
                    if (errorIsStatus(err, 404)) {
                        this.handleNoDailyBalanceRecords({fetchStateKey});
                        return;
                    }
                    storeHelper.handleError({ error: err, rethrow: false });
                });
        },
        loadTransactionsForDay (date: string): Promise<any> {
            // already loading ANYTHING then ditch out
            if (this.fetchState === FetchState.Loading) return Promise.resolve();

            // assume page in dictionary is a loaded page
            if (this.dailyBalancesTransactionsDictionary &&
                this.dailyBalancesTransactionsDictionary[date]) return Promise.resolve();

            this.setStateLoading();

            return bankService.getTransactionsForDay(date)
                .then(response => {
                    this.setDailyBalancesTransactionsDictionary(response.data);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        reloadPredictedDailyBalances () {
            this.setPredictedBalancesStateUnloaded();
            this.loadPredictedDailyBalances();
        },
        loadPredictedDailyBalances (): Promise<any> {
            if (this.predictedDailyBalancesFetchState === FetchState.Loading) return Promise.resolve();
            if (this.predictedDailyBalancesFetchState === FetchState.Loaded) return Promise.resolve();

            this.setPredictedBalancesStateLoading();

            return payrollService.getPredictedContributions()
                .then(response => {
                    this.setPredictedDailyBalances(response.data.contributions);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        handleNoDailyBalanceRecords (params: {
            stateKey: FetchStateKey, firstRecordDate: string,
            accountStartDate: string, periodStartBalance?: number}): void {
            const currentDateString = moment().format("YYYY-MM-DD");
            const zeroBalanceData: BankTransactionDailyBalancePage = {
                accountStartDate: params.accountStartDate || currentDateString,
                periodStartBalance: params.periodStartBalance || 0,
                records: [ {
                    balance: params.periodStartBalance || 0,
                    date: params.firstRecordDate || currentDateString,
                    timestamp: moment(currentDateString).unix()
                } ]
            };

            this.setHsaAccountStatus({ stateKey: params.stateKey, accountStatus: false });
            this.setDailyBalancesState({ stateKey: params.stateKey, balanceData: zeroBalanceData });
        },
        handleNoInstantDailyBalance (stateKey: FetchStateKey): void {
            const zeroInstantBalance: BalanceResponse = {
                balances: [ {
                    type: BalanceType.Available,
                    amount: 0
                } ]
            };
            this.setHsaAccountStatus({ stateKey, accountStatus: false });
            this.setInstantBalance({ stateKey, balanceData: zeroInstantBalance });
        },
        loadBalanceOnFile (params?: { username: string, partnerId: number }): Promise<any> {
            const userKeyValue: FetchStateKey = params || { username: null, partnerId: 0 };

            this.invalidateBalanceFetchState(userKeyValue);

            const currentFetchState = this.balanceOnFile.fetchState;
            if (currentFetchState === FetchState.Loading || currentFetchState === FetchState.Loaded) {
                return Promise.resolve();
            }

            this.setBalanceOnFileLoading(userKeyValue);

            return bankService.getCurrentHsaBalance(userKeyValue.username, userKeyValue.partnerId)
                .then(response => {
                    this.setBalanceOnFile(response.data);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: false }));
        }
    }
});